"use client";

import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icon";
import { PaginationRoot, usePaginationContext } from "./context";
import {
  buildEllipsisPages,
  buildUploadLeadingPages,
  buildWindowPages,
  type WindowAlign,
} from "./pagination-utils";
import {
  navRowVariants,
  pageNumberVariants,
  stepIconVariants,
  stepLabeledVariants,
  stepLabelVariants,
  stepLabelIconVariants,
} from "./variants";

export function PaginationNav({
  className,
  ...props
}: React.ComponentProps<"nav">) {
  const { variant } = usePaginationContext();
  return (
    <nav
      aria-label="Pagination"
      className={cn(navRowVariants({ variant }), className)}
      {...props}
    />
  );
}

export function PaginationRow({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { variant } = usePaginationContext();
  return (
    <div className={cn(navRowVariants({ variant }), className)} {...props} />
  );
}

/** First page — double chevron, styled per variant. */
export function PaginationFirst({ className }: { className?: string }) {
  const { atFirst, disabled, onPageChange, variant } = usePaginationContext();
  return (
    <button
      type="button"
      aria-label="Go to first page"
      aria-disabled={atFirst || disabled}
      disabled={atFirst || disabled}
      onClick={() => onPageChange(1)}
      className={cn(
        pageNumberVariants({ variant }),
        "disabled:opacity-30",
        className,
      )}
    >
      <Icon name="IconChevronDoubleLeft" className="text-contrast/70 size-4" />
    </button>
  );
}

/** Last page — double chevron. */
export function PaginationLast({ className }: { className?: string }) {
  const { totalPages, atLast, disabled, onPageChange, variant } =
    usePaginationContext();
  return (
    <button
      type="button"
      aria-label="Go to last page"
      aria-disabled={atLast || disabled}
      disabled={atLast || disabled}
      onClick={() => onPageChange(totalPages)}
      className={cn(
        pageNumberVariants({ variant }),
        "disabled:opacity-30",
        className,
      )}
    >
      <Icon name="IconChevronDoubleRight" className="text-contrast/70 size-4" />
    </button>
  );
}

export type PaginationStepMode = "icon" | "labeled";

export interface PaginationPrevProps {
  mode?: PaginationStepMode;
  className?: string;
  /** When mode is labeled, text after icon (default "Prev"). */
  children?: React.ReactNode;
}

export function PaginationPrev({
  mode = "labeled",
  className,
  children = "Prev",
}: PaginationPrevProps) {
  const { page, atFirst, disabled, onPageChange, variant } =
    usePaginationContext();

  if (mode === "icon") {
    return (
      <button
        type="button"
        aria-label="Go to previous page"
        aria-disabled={atFirst || disabled}
        disabled={atFirst || disabled}
        onClick={() => onPageChange(Math.max(1, page - 1))}
        className={cn(stepIconVariants({ variant }), className)}
      >
        <Icon name="IconChevronLeft" size={14} className="text-foreground" />
      </button>
    );
  }

  return (
    <button
      type="button"
      aria-label="Go to previous page"
      disabled={atFirst || disabled}
      onClick={() => onPageChange(Math.max(1, page - 1))}
      className={cn(stepLabeledVariants({ variant }), className)}
    >
      <Icon
        name="IconChevronLeft"
        size={14}
        className={stepLabelIconVariants({ variant, direction: "prev" })}
      />
      <span className={stepLabelVariants({ variant, direction: "prev" })}>
        {children}
      </span>
    </button>
  );
}

export interface PaginationNextProps {
  mode?: PaginationStepMode;
  className?: string;
  children?: React.ReactNode;
}

export function PaginationNext({
  mode = "labeled",
  className,
  children = "Next",
}: PaginationNextProps) {
  const { page, totalPages, atLast, disabled, onPageChange, variant } =
    usePaginationContext();

  if (mode === "icon") {
    return (
      <button
        type="button"
        aria-label="Go to next page"
        aria-disabled={atLast || disabled}
        disabled={atLast || disabled}
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        className={cn(stepIconVariants({ variant }), className)}
      >
        <Icon name="IconChevronRight" size={14} className="text-foreground" />
      </button>
    );
  }

  return (
    <button
      type="button"
      aria-label="Go to next page"
      disabled={atLast || disabled}
      onClick={() => onPageChange(Math.min(totalPages, page + 1))}
      className={cn(stepLabeledVariants({ variant }), className)}
    >
      <span className={stepLabelVariants({ variant, direction: "next" })}>
        {children}
      </span>
      <Icon
        name="IconChevronRight"
        size={14}
        className={stepLabelIconVariants({ variant, direction: "next" })}
      />
    </button>
  );
}

/** Compact tool-card prev/next (no text). */
export function PaginationPrevCompact({ className }: { className?: string }) {
  const { page, atFirst, disabled, onPageChange } = usePaginationContext();
  return (
    <button
      type="button"
      aria-label="Previous page"
      disabled={atFirst || disabled}
      onClick={() => onPageChange(Math.max(1, page - 1))}
      className={cn(
        "flex items-center justify-center size-5 rounded-sm border border-border/60 text-contrast/70",
        "enabled:hover:bg-contrast/4 transition-colors",
        "disabled:opacity-40",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
        className,
      )}
    >
      <Icon name="IconChevronLeft" size={10} aria-hidden />
    </button>
  );
}

export function PaginationNextCompact({ className }: { className?: string }) {
  const { page, totalPages, atLast, disabled, onPageChange } =
    usePaginationContext();
  return (
    <button
      type="button"
      aria-label="Next page"
      disabled={atLast || disabled}
      onClick={() => onPageChange(Math.min(totalPages, page + 1))}
      className={cn(
        "flex items-center justify-center size-5 rounded-sm border border-border/60 text-contrast/70",
        "enabled:hover:bg-contrast/4 transition-colors",
        "disabled:opacity-40",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
        className,
      )}
    >
      <Icon name="IconChevronRight" size={10} aria-hidden />
    </button>
  );
}

export interface PaginationPagesProps {
  strategy: "ellipsis" | "window" | "all" | "current" | "uploadLeading";
  maxVisible?: number;
  /** For `strategy="window"`: centered window (jobs list) vs near-current (search). */
  windowAlign?: WindowAlign;
  className?: string;
  ellipsisClassName?: string;
}

export function PaginationPages({
  strategy,
  maxVisible = 4,
  windowAlign = "near",
  className,
  ellipsisClassName,
}: PaginationPagesProps) {
  const { page, totalPages, disabled, onPageChange, variant } =
    usePaginationContext();

  if (strategy === "current") {
    return (
      <button
        type="button"
        aria-current="page"
        aria-label={`Page ${page} of ${totalPages}`}
        disabled={disabled}
        className={cn(pageNumberVariants({ variant, active: true }), className)}
      >
        {page}
      </button>
    );
  }

  const items: (number | "ellipsis")[] =
    strategy === "ellipsis"
      ? buildEllipsisPages(page, totalPages)
      : strategy === "all"
        ? Array.from({ length: totalPages }, (_, i) => i + 1)
        : strategy === "uploadLeading"
          ? buildUploadLeadingPages(totalPages, maxVisible)
          : buildWindowPages(page, totalPages, maxVisible, windowAlign);

  const ellipsisChar =
    variant === "table" || strategy === "uploadLeading" ? "..." : "…";

  // The page-number group's gap should breathe against the bordered
  // size-7 buttons we render for `search`/`list`/`arbitrator`. The previous
  // `gap-0.5` (2px) made the buttons read as a single mashed-together
  // block (`1234` instead of `1 2 3 4`) which the talent-search results
  // footer made obvious. Variants that render unbordered numbers (e.g.
  // `compact`, `integrations`) keep the tight gap.
  const pagesGapClass =
    variant === "table"
      ? "gap-1"
      : variant === "search" ||
          variant === "list" ||
          variant === "arbitrator" ||
          variant === "sheet" ||
          variant === "prompt"
        ? "gap-1"
        : "gap-0.5";

  return (
    <div className={cn("flex items-center", pagesGapClass, className)}>
      {items.map((p, idx) =>
        p === "ellipsis" ? (
          <span
            key={`ellipsis-${idx}`}
            aria-hidden
            className={cn(
              variant === "table"
                ? "flex h-8 w-8 items-center justify-center text-sm font-medium text-contrast/50"
                : variant === "arbitrator"
                  ? "flex size-7 items-center justify-center text-sm text-contrast/40"
                  : "flex size-7 items-center justify-center text-sm text-contrast/40",
              ellipsisClassName,
            )}
          >
            {ellipsisChar}
          </span>
        ) : (
          <button
            key={p}
            type="button"
            aria-label={`Page ${p}`}
            aria-current={p === page ? "page" : undefined}
            disabled={disabled}
            onClick={() => onPageChange(p)}
            className={cn(pageNumberVariants({ variant, active: p === page }))}
          >
            {p}
          </button>
        ),
      )}
    </div>
  );
}

export const Pagination = {
  Root: PaginationRoot,
  Nav: PaginationNav,
  Row: PaginationRow,
  First: PaginationFirst,
  Last: PaginationLast,
  Prev: PaginationPrev,
  Next: PaginationNext,
  PrevCompact: PaginationPrevCompact,
  NextCompact: PaginationNextCompact,
  Pages: PaginationPages,
};
